import type { ICallOptions } from '@telnyx/webrtc';

export const TERMINAL_TELNYX_CALL_STATES = new Set(['hangup', 'destroy', 'purge']);

export interface TelnyxBrowserCallSnapshot {
  status: string;
  isMuted: boolean;
  hasCall: boolean;
}

interface TelnyxCallLike {
  id: string;
  state: string;
  isAudioMuted: boolean;
  hangup(): Promise<void>;
  muteAudio(): void;
  unmuteAudio(): void;
}

interface TelnyxNotificationLike {
  type: string;
  call?: TelnyxCallLike;
  error?: unknown;
  errorMessage?: string;
}

interface TelnyxErrorLike {
  error?: unknown;
  message?: string;
}

interface TelnyxClientLike {
  remoteElement: HTMLMediaElement | string | Function;
  on(eventName: string, callback: (event: never) => void): this;
  off(eventName: string, callback?: (event: never) => void): this;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  newCall(options: ICallOptions): TelnyxCallLike;
}

interface TelnyxClientConstructor {
  new (options: { login_token: string }): TelnyxClientLike;
}

export interface TelnyxSdkModule {
  TelnyxRTC: TelnyxClientConstructor;
}

export type TelnyxSdkLoader = () => Promise<TelnyxSdkModule>;

export interface TelnyxBrowserCallOptions {
  token: string;
  destinationNumber: string;
  callerNumber: string;
  callerName?: string;
  remoteElement: HTMLMediaElement;
  micId?: string;
  speakerId?: string;
  onUpdate(snapshot: TelnyxBrowserCallSnapshot): void;
  onDialStarted?(): void;
  onError?(message: string): void;
  readyTimeoutMs?: number;
  loadSdk?: TelnyxSdkLoader;
}

/**
 * A failed browser start may safely hand off to `tel:` only before `newCall`
 * has been invoked. Afterwards a SIP INVITE may already be in flight, so a
 * second dial attempt would risk calling the same person twice.
 */
export class TelnyxBrowserCallError extends Error {
  readonly safeToFallback: boolean;

  constructor(message: string, safeToFallback: boolean) {
    super(message);
    this.name = 'TelnyxBrowserCallError';
    this.safeToFallback = safeToFallback;
  }
}

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string' && error.trim()) return error;
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object') {
    const value = error as TelnyxErrorLike;
    if (value.error && value.error !== error) return errorMessage(value.error, fallback);
    if (typeof value.message === 'string' && value.message.trim()) return value.message;
  }
  return fallback;
}

async function defaultSdkLoader(): Promise<TelnyxSdkModule> {
  return (await import('@telnyx/webrtc')) as unknown as TelnyxSdkModule;
}

/** Owns exactly one TelnyxRTC client and at most one outbound browser call. */
export class TelnyxBrowserCall {
  private readonly options: TelnyxBrowserCallOptions;
  private client: TelnyxClientLike | null = null;
  private call: TelnyxCallLike | null = null;
  private disposed = false;
  private dialRequested = false;
  private status = 'connecting';
  private cancelPendingStart: ((reason: TelnyxBrowserCallError) => void) | null = null;
  private resolvePendingReady: (() => void) | null = null;

  private readonly onReady = () => this.resolvePendingReady?.();
  private readonly onSessionError = (event: TelnyxErrorLike) => {
    const failure = new TelnyxBrowserCallError(
      errorMessage(event, 'Telnyx browser calling failed.'),
      !this.dialRequested,
    );
    if (this.cancelPendingStart) {
      this.cancelPendingStart(failure);
      return;
    }
    this.failActiveCall(failure.message);
  };
  private readonly onNotification = (notification: TelnyxNotificationLike) => {
    if (notification.type === 'callUpdate' && notification.call) {
      const updatedCall = notification.call;
      if (this.call && updatedCall.id !== this.call.id) return;
      if (!this.call && !this.dialRequested) return;

      this.call = updatedCall;
      this.status = updatedCall.state;
      this.emit();
      if (TERMINAL_TELNYX_CALL_STATES.has(updatedCall.state)) {
        this.call = null;
        void this.shutdown(false).catch(() => undefined);
      }
      return;
    }

    if (
      notification.type === 'userMediaError' ||
      notification.type === 'peerConnectionFailureError' ||
      notification.type === 'signalingStateClosed'
    ) {
      this.failActiveCall(
        errorMessage(
          notification.error ?? notification.errorMessage,
          notification.type === 'userMediaError'
            ? 'LeadForge could not access the microphone.'
            : 'The browser audio connection failed.',
        ),
      );
    }
  };

  constructor(options: TelnyxBrowserCallOptions) {
    this.options = options;
  }

  get hasCall(): boolean {
    return !!this.call && !TERMINAL_TELNYX_CALL_STATES.has(this.status);
  }

  async start(): Promise<void> {
    if (this.disposed) throw new TelnyxBrowserCallError('Browser call was cancelled.', true);

    const loadSdk = this.options.loadSdk ?? defaultSdkLoader;
    let client: TelnyxClientLike;
    try {
      const { TelnyxRTC } = await loadSdk();
      if (this.disposed) throw new TelnyxBrowserCallError('Browser call was cancelled.', true);
      client = new TelnyxRTC({ login_token: this.options.token });
    } catch (error) {
      if (error instanceof TelnyxBrowserCallError) throw error;
      throw new TelnyxBrowserCallError(errorMessage(error, 'Could not load Telnyx browser calling.'), true);
    }

    this.client = client;
    client.remoteElement = this.options.remoteElement;

    let readySettled = false;
    let readyTimer: ReturnType<typeof setTimeout> | null = null;
    const ready = new Promise<void>((resolve, reject) => {
      const settle = (action: () => void) => {
        if (readySettled) return;
        readySettled = true;
        this.cancelPendingStart = null;
        this.resolvePendingReady = null;
        if (readyTimer) clearTimeout(readyTimer);
        action();
      };
      this.cancelPendingStart = (reason) => settle(() => reject(reason));
      this.resolvePendingReady = () => settle(resolve);
      readyTimer = setTimeout(
        () =>
          settle(() =>
            reject(new TelnyxBrowserCallError('Telnyx browser audio timed out while connecting.', true)),
          ),
        this.options.readyTimeoutMs ?? 10_000,
      );

    });

    client
      .on('telnyx.ready', this.onReady as (event: never) => void)
      .on('telnyx.error', this.onSessionError as (event: never) => void)
      .on('telnyx.notification', this.onNotification as (event: never) => void);

    try {
      await Promise.all([client.connect(), ready]);
      if (this.disposed) throw new TelnyxBrowserCallError('Browser call was cancelled.', true);

      // From this line onward fallback is deliberately unsafe. newCall() starts
      // the SDK's microphone/peer setup and may send the outbound INVITE before
      // any subsequent error reaches application code.
      this.dialRequested = true;
      this.options.onDialStarted?.();
      this.call = client.newCall({
        destinationNumber: this.options.destinationNumber,
        callerNumber: this.options.callerNumber,
        ...(this.options.callerName ? { callerName: this.options.callerName } : {}),
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        ...(this.options.micId ? { micId: this.options.micId } : {}),
        ...(this.options.speakerId ? { speakerId: this.options.speakerId } : {}),
        video: false,
        remoteElement: this.options.remoteElement,
      });
      this.status = this.call.state || 'trying';
      this.emit();
    } catch (error) {
      const failure =
        error instanceof TelnyxBrowserCallError
          ? error
          : new TelnyxBrowserCallError(
              errorMessage(error, 'Telnyx browser calling failed.'),
              !this.dialRequested,
            );
      await this.shutdown(this.dialRequested).catch(() => undefined);
      throw failure;
    } finally {
      client.off('telnyx.ready', this.onReady as (event: never) => void);
    }
  }

  toggleMute(): void {
    const call = this.call;
    if (!call || this.disposed) return;
    const nextMuted = !call.isAudioMuted;
    if (nextMuted) call.muteAudio();
    else call.unmuteAudio();
    this.emit({ isMuted: nextMuted });
  }

  async hangup(): Promise<void> {
    if (this.disposed) return;
    this.status = 'hangup';
    this.emit({ hasCall: false, isMuted: false });
    await this.shutdown(true);
  }

  async dispose(): Promise<void> {
    await this.shutdown(true);
  }

  private emit(overrides: Partial<TelnyxBrowserCallSnapshot> = {}): void {
    if (this.disposed) return;
    this.options.onUpdate({
      status: this.status,
      isMuted: this.call?.isAudioMuted ?? false,
      hasCall: this.hasCall,
      ...overrides,
    });
  }

  private failActiveCall(message: string): void {
    if (this.disposed) return;
    this.status = 'failed';
    this.emit({ hasCall: false });
    this.options.onError?.(message);
    void this.shutdown(true).catch(() => undefined);
  }

  private async shutdown(hangup: boolean): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelPendingStart?.(new TelnyxBrowserCallError('Browser call was cancelled.', true));
    this.cancelPendingStart = null;

    const call = this.call;
    const client = this.client;
    this.call = null;
    this.client = null;
    if (client) {
      client.off('telnyx.error', this.onSessionError as (event: never) => void);
      client.off('telnyx.notification', this.onNotification as (event: never) => void);
    }

    let firstError: unknown;
    if (hangup && call) {
      try {
        await call.hangup();
      } catch (error) {
        firstError = error;
      }
    }
    if (client) {
      try {
        await client.disconnect();
      } catch (error) {
        firstError ??= error;
      }
    }
    if (firstError) throw firstError;
  }
}
